// services/api/client.js
import { API_CONFIG, getFullUrl } from './config';
import { sessionManager } from '../../stores/sessionManager';
import { demoMode, DEMO_DATA, simulateDelay } from '../../stores/demoMode';

class ApiError extends Error {
    constructor(message, status, headers = null, response = null) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.headers = headers;
        this.response = response;
        Object.setPrototypeOf(this, ApiError.prototype);
    }
}

class ApiClient {
    constructor() {
        this.defaultHeaders = {
            'Content-Type': 'application/json'
        };
        this.maxRetries = 3;
        this.retryDelay = 1000;
        // Define endpoints that don't require authentication
        this.authEndpoints = ['/login', '/register', '/refresh'];
    }

    getToken(key) {
        // Check both sessionStorage and localStorage for tokens
        return sessionStorage.getItem(key) || localStorage.getItem(key);
    }

    async request(endpoint, options = {}, retryCount = 0) {
        // Check if we're in demo mode
        if (demoMode.isEnabled()) {
            return this.handleDemoRequest(endpoint, options);
        }

        const url = getFullUrl(endpoint);
        const accessToken = this.getToken('accessToken');
        let headers = { ...this.defaultHeaders };

        // Add explicit mode for CORS handling
        const fetchOptions = {
            ...options,
            credentials: 'include', // Send cookies for CORS
            mode: 'cors', // Explicitly request CORS
        };

        // Immediate session check for non-auth endpoints
        if (!accessToken && !this.authEndpoints.some(authPath => endpoint.includes(authPath))) {
            sessionManager.handleSessionExpired(
                new ApiError('Your session has expired. Please reload the page to log back in.', 401)
            );
            return;
        }

        if (options.body instanceof FormData) {
            delete headers['Content-Type'];
        }

        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        }

        // Handle FormData requests with progress tracking
        if (options.onProgress && options.body instanceof FormData) {
            return this.handleFormDataRequest(url, headers, options, retryCount);
        }

        try {
            const response = await fetch(url, {
                ...fetchOptions,
                headers: {
                    ...headers,
                    ...options.headers
                }
            });

            if (!response.ok) {
                const error = await this.handleErrorResponse(response);

                // Handle token expiration
                if (error.status === 401 && accessToken) {
                    try {
                        await this.refreshToken();
                        return this.request(endpoint, options);
                    } catch (refreshError) {
                        this.clearAuth();
                        sessionManager.handleSessionExpired(
                            new ApiError('Your session has expired. Please reload the page to log back in.', 401)
                        );
                        return;
                    }
                }

                // Handle rate limiting
                if (error.status === 429 && retryCount < this.maxRetries) {
                    const retryAfter = parseInt(error.headers?.get('Retry-After') || '1');
                    const delay = this.calculateRetryDelay(retryCount, retryAfter);
                    await this.delay(delay);
                    return this.request(endpoint, options, retryCount + 1);
                }

                throw error;
            }

            const contentType = response.headers.get('content-type');
            return contentType?.includes('application/json') ? 
                   await response.json() : 
                   await response.text();
        } catch (error) {
            throw error;
        }
    }

    async handleFormDataRequest(url, headers, options, retryCount) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open(options.method || 'GET', url);

            Object.entries({ ...headers, ...options.headers }).forEach(([key, value]) => {
                xhr.setRequestHeader(key, value);
            });

            xhr.upload.onprogress = options.onProgress;

            xhr.onload = async () => {
                try {
                    const response = JSON.parse(xhr.responseText);
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve(response);
                    } else if (xhr.status === 401) {
                        try {
                            await this.refreshToken();
                            const result = await this.request(url, options);
                            resolve(result);
                        } catch (refreshError) {
                            this.clearAuth();
                            // Show session expired modal for file uploads too
                            sessionManager.handleSessionExpired(
                                new ApiError('Your session has expired. Please reload the page to log back in.', 401)
                            );
                            reject(refreshError);
                        }
                    } else if (xhr.status === 429 && retryCount < this.maxRetries) {
                        const retryAfter = parseInt(xhr.getResponseHeader('Retry-After') || '1');
                        const delay = this.calculateRetryDelay(retryCount, retryAfter);
                        await this.delay(delay);
                        try {
                            const result = await this.request(url, options, retryCount + 1);
                            resolve(result);
                        } catch (error) {
                            reject(error);
                        }
                    } else {
                        reject(new ApiError(response.detail || response.error || 'Request failed', xhr.status));
                    }
                } catch (e) {
                    reject(new ApiError('Invalid server response', xhr.status));
                }
            };

            xhr.onerror = () => {
                reject(new ApiError('Network error', 0));
            };

            xhr.send(options.body);
        });
    }

    // Demo mode request handler - returns mock data for portfolio showcase
    async handleDemoRequest(endpoint, options = {}) {
        await simulateDelay(200 + Math.random() * 300); // Simulate realistic API latency

        const method = options.method || 'GET';

        // Parse endpoint to determine what data to return
        if (endpoint.includes('/clients')) {
            const idMatch = endpoint.match(/\/clients\/(\d+)/);
            if (method === 'GET') {
                if (idMatch) {
                    const client = DEMO_DATA.clients.find(c => c.id === parseInt(idMatch[1]));
                    return client || { error: 'Client not found' };
                }
                return DEMO_DATA.clients;
            }
            if (method === 'POST') {
                const data = JSON.parse(options.body || '{}');
                const newClient = { id: DEMO_DATA.clients.length + 1, ...data, created_at: new Date().toISOString() };
                DEMO_DATA.clients.push(newClient);
                return newClient;
            }
            if ((method === 'PUT' || method === 'PATCH') && idMatch) {
                const id = parseInt(idMatch[1]);
                const idx = DEMO_DATA.clients.findIndex(c => c.id === id);
                if (idx !== -1) {
                    const data = JSON.parse(options.body || '{}');
                    DEMO_DATA.clients[idx] = { ...DEMO_DATA.clients[idx], ...data };
                    return DEMO_DATA.clients[idx];
                }
                return { error: 'Client not found' };
            }
            if (method === 'DELETE' && idMatch) {
                const id = parseInt(idMatch[1]);
                const idx = DEMO_DATA.clients.findIndex(c => c.id === id);
                if (idx !== -1) {
                    DEMO_DATA.clients.splice(idx, 1);
                    return { success: true };
                }
                return { error: 'Client not found' };
            }
        }

        if (endpoint.includes('/workers')) {
            const idMatch = endpoint.match(/\/workers\/(\d+)/);
            if (method === 'GET') {
                if (idMatch) {
                    const worker = DEMO_DATA.workers.find(w => w.id === parseInt(idMatch[1]));
                    return worker || { error: 'Worker not found' };
                }
                return DEMO_DATA.workers;
            }
            if (method === 'POST') {
                const data = JSON.parse(options.body || '{}');
                const newWorker = { id: DEMO_DATA.workers.length + 1, ...data, created_at: new Date().toISOString() };
                DEMO_DATA.workers.push(newWorker);
                return newWorker;
            }
            if ((method === 'PUT' || method === 'PATCH') && idMatch) {
                const id = parseInt(idMatch[1]);
                const idx = DEMO_DATA.workers.findIndex(w => w.id === id);
                if (idx !== -1) {
                    const data = JSON.parse(options.body || '{}');
                    DEMO_DATA.workers[idx] = { ...DEMO_DATA.workers[idx], ...data };
                    return DEMO_DATA.workers[idx];
                }
                return { error: 'Worker not found' };
            }
            if (method === 'DELETE' && idMatch) {
                const id = parseInt(idMatch[1]);
                const idx = DEMO_DATA.workers.findIndex(w => w.id === id);
                if (idx !== -1) {
                    DEMO_DATA.workers.splice(idx, 1);
                    return { success: true };
                }
                return { error: 'Worker not found' };
            }
        }

        if (endpoint.includes('/projects')) {
            const idMatch = endpoint.match(/\/projects\/(\d+)/);
            if (method === 'GET') {
                if (idMatch) {
                    const project = DEMO_DATA.projects.find(p => p.id === parseInt(idMatch[1]));
                    return project || { error: 'Project not found' };
                }
                return DEMO_DATA.projects;
            }
            if (method === 'POST') {
                const data = JSON.parse(options.body || '{}');
                const client = DEMO_DATA.clients.find(c => c.id === data.client);
                const newProject = {
                    id: DEMO_DATA.projects.length + 1,
                    ...data,
                    client_name: client?.name || '',
                    created_at: new Date().toISOString(),
                    progress: 0
                };
                DEMO_DATA.projects.push(newProject);
                return newProject;
            }
            if ((method === 'PUT' || method === 'PATCH') && idMatch) {
                const id = parseInt(idMatch[1]);
                const idx = DEMO_DATA.projects.findIndex(p => p.id === id);
                if (idx !== -1) {
                    const data = JSON.parse(options.body || '{}');
                    if (data.client) {
                        const client = DEMO_DATA.clients.find(c => c.id === data.client);
                        data.client_name = client?.name || '';
                    }
                    DEMO_DATA.projects[idx] = { ...DEMO_DATA.projects[idx], ...data };
                    return DEMO_DATA.projects[idx];
                }
                return { error: 'Project not found' };
            }
            if (method === 'DELETE' && idMatch) {
                const id = parseInt(idMatch[1]);
                const idx = DEMO_DATA.projects.findIndex(p => p.id === id);
                if (idx !== -1) {
                    DEMO_DATA.projects.splice(idx, 1);
                    return { success: true };
                }
                return { error: 'Project not found' };
            }
        }

        if (endpoint.includes('/work-orders')) {
            const idMatch = endpoint.match(/\/work-orders\/(\d+)/);
            if (method === 'GET') {
                if (idMatch) {
                    const workOrder = DEMO_DATA.workOrders.find(wo => wo.id === parseInt(idMatch[1]));
                    return workOrder || { error: 'Work order not found' };
                }
                return DEMO_DATA.workOrders;
            }
            if (method === 'POST') {
                const data = JSON.parse(options.body || '{}');
                const newWorkOrder = { id: DEMO_DATA.workOrders.length + 1, ...data, created_at: new Date().toISOString(), actual_hours: 0 };
                DEMO_DATA.workOrders.push(newWorkOrder);
                return newWorkOrder;
            }
            if ((method === 'PUT' || method === 'PATCH') && idMatch) {
                const id = parseInt(idMatch[1]);
                const idx = DEMO_DATA.workOrders.findIndex(wo => wo.id === id);
                if (idx !== -1) {
                    const data = JSON.parse(options.body || '{}');
                    DEMO_DATA.workOrders[idx] = { ...DEMO_DATA.workOrders[idx], ...data };
                    return DEMO_DATA.workOrders[idx];
                }
                return { error: 'Work order not found' };
            }
            if (method === 'DELETE' && idMatch) {
                const id = parseInt(idMatch[1]);
                const idx = DEMO_DATA.workOrders.findIndex(wo => wo.id === id);
                if (idx !== -1) {
                    DEMO_DATA.workOrders.splice(idx, 1);
                    return { success: true };
                }
                return { error: 'Work order not found' };
            }
        }

        // Default: return empty array for GET, success for POST
        if (method === 'GET') return [];
        return { success: true };
    }

    // Helper methods remain largely unchanged
    async handleErrorResponse(response) {
        let errorMessage = `Error ${response.status}`;
        let responseData = null;
        
        try {
            responseData = await response.json();
            
            // Check if this is a Django validation error (could be an object with field errors)
            if (typeof responseData === 'object') {
                // Handle field validation errors
                if (!responseData.detail && !responseData.message) {
                    // Check if there are any field errors
                    const hasFieldErrors = Object.keys(responseData).some(key => {
                        return Array.isArray(responseData[key]) || 
                               typeof responseData[key] === 'string' || 
                               typeof responseData[key] === 'object';
                    });
                    
                    if (hasFieldErrors) {
                        errorMessage = 'Validation error';
                        // We'll keep the full responseData object for field-specific error handling
                    }
                } else {
                    // Use the provided error message
                    errorMessage = responseData.detail || responseData.message || errorMessage;
                }
                
                // Check for non_field_errors array (Django's common pattern)
                if (responseData.non_field_errors && Array.isArray(responseData.non_field_errors)) {
                    errorMessage = responseData.non_field_errors[0];
                }
            }
        } catch (e) {
            try {
                const text = await response.text();
                if (text) errorMessage = text;
            } catch (textError) {
                // Could not parse error response
            }
        }
        
        // Return the error with the full response data for field-specific error handling
        return new ApiError(errorMessage, response.status, response.headers, responseData);
    }

    async refreshToken() {
        const refreshToken = this.getToken('refreshToken');
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        // Determine which storage to use based on rememberMe preference
        const rememberMe = localStorage.getItem('rememberMe') === 'true';
        const storage = rememberMe ? localStorage : sessionStorage;

        const response = await fetch(getFullUrl(API_CONFIG.ENDPOINTS.AUTH.REFRESH), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh: refreshToken })
        });

        if (!response.ok) {
            throw new Error('Token refresh failed');
        }

        const data = await response.json();

        // Store the new access token in the same storage location
        storage.setItem('accessToken', data.access);

        // If refresh token is rotated, update it too
        if (data.refresh) {
            storage.setItem('refreshToken', data.refresh);
        }

        return data.access;
    }

    calculateRetryDelay(retryCount, retryAfter = 1) {
        const exponentialDelay = Math.min(
            this.retryDelay * Math.pow(2, retryCount) + Math.random() * 1000,
            30000
        );
        return Math.max(retryAfter * 1000, exponentialDelay);
    }

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    clearAuth() {
        // Clear from both storage locations
        ['accessToken', 'refreshToken', 'username', 'rememberMe', 'sessionExpiry'].forEach(key => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });
    }
}

export const apiClient = new ApiClient();